/*
 * The pipeline's linear "happy path" through the 17-state machine, expressed as
 * an ordered list of milestones the progress view walks. Control/branch states
 * (paused, needs_reauth, blocked, recovering, recheck_pending, failed, aborted)
 * are NOT path milestones — they are overlays the current state can sit in — so
 * they're surfaced separately rather than as a step in the line.
 *
 * Each milestone groups the in-progress + settled state pair from the diagram
 * (e.g. PREPPING→PREPPED) under one operator-facing label, so the progress rail
 * reads as a short, legible sequence instead of all 17 raw enum values.
 */

import { runStatusMeta, type RunState } from "../../lib/status";

export type StageStatus = "done" | "active" | "pending";

export interface PipelineStage {
  /** Stable key. */
  key: string;
  /** Operator-facing label. */
  label: string;
  /** Diagram states that map onto this milestone (in order). */
  states: RunState[];
}

/**
 * The ordered milestones. AUDITING can settle clean (→ bookkeeping → completed)
 * or with discrepancies (→ recovering, looping back to auditing); we render the
 * audit milestone once and surface the clean/discrepancy outcome via the run
 * status badge + the audit overlay, keeping the rail linear.
 */
export const PIPELINE_STAGES: readonly PipelineStage[] = [
  { key: "queued", label: "Queued", states: ["queued", "blocked"] },
  { key: "prep", label: "Prep", states: ["prepping", "prepped"] },
  { key: "upload", label: "Upload", states: ["uploading", "uploaded"] },
  {
    key: "finalize",
    label: "Facebook",
    states: ["finalizing", "finalized"],
  },
  {
    key: "audit",
    label: "Verify",
    states: [
      "auditing",
      "recheck_pending",
      "recovering",
      "audited_clean",
      "audited_with_discrepancies",
    ],
  },
  { key: "bookkeep", label: "Notion", states: ["bookkeeping"] },
  { key: "done", label: "Complete", states: ["completed"] },
] as const;

/* Overlay states — a run can be *in* one of these while sitting on a milestone;
 * they are not their own step. `failed`/`aborted` are terminal off-path ends. */
const OVERLAY_STATES: ReadonlySet<RunState> = new Set<RunState>([
  "paused",
  "needs_reauth",
  "blocked",
  "recheck_pending",
  "recovering",
  "failed",
  "aborted",
]);

function norm(value: string): RunState {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_") as RunState;
}

/**
 * The backend `runs.current_stage` vocabulary (the `Stage` enum:
 * prep/upload/finalize/audit/recover/bookkeep) → milestone key. Used as a
 * fallback locator when the run *status* is an overlay (paused/needs_reauth)
 * that doesn't itself map onto a milestone, so the rail still freezes at the
 * stage the run was parked in.
 */
const STAGE_TO_MILESTONE: Record<string, string> = {
  prep: "prep",
  upload: "upload",
  finalize: "finalize",
  audit: "audit",
  recover: "audit",
  bookkeep: "bookkeep",
};

/** Index of the milestone a given run state currently sits on (or -1). */
function stageIndexFor(state: RunState): number {
  return PIPELINE_STAGES.findIndex((stage) => stage.states.includes(state));
}

/** Index of the milestone a `current_stage` string maps onto (or -1). */
function milestoneIndexForStage(currentStage: string | null | undefined): number {
  if (!currentStage) return -1;
  const key = STAGE_TO_MILESTONE[norm(currentStage)];
  if (!key) return -1;
  return PIPELINE_STAGES.findIndex((s) => s.key === key);
}

export interface ResolvedStage extends PipelineStage {
  status: StageStatus;
}

/**
 * Walk the milestone list for a run `status`, marking each milestone done /
 * active / pending. A terminal `completed` marks the whole rail done; a failure
 * (`failed`/`aborted`) freezes the rail at the furthest reached milestone (the
 * step where it died stays `active` so the operator sees where it stopped).
 */
export function resolvePipeline(
  status: string,
  currentStage?: string | null,
): ResolvedStage[] {
  const state = norm(status);

  // Map an overlay/branch state back onto its underlying milestone so the rail
  // still advances (e.g. RECOVERING sits on the Audit milestone). When the
  // status itself is an off-milestone overlay (paused/needs_reauth/failed), fall
  // back to the `current_stage` hint so the rail freezes where it stopped.
  const activeIndex =
    stageIndexFor(state) !== -1
      ? stageIndexFor(state)
      : milestoneIndexForStage(currentStage);

  // A SUCCESS-terminal → the whole rail is done. Both a real launch (`completed`)
  // and a Draft (`drafted`) are successful ends: a Draft created the AdManage draft
  // + marked Notion, it just skipped the Meta push (Facebook/Verify). Without
  // `drafted` here it falls through to the freeze branch below and renders stalled at
  // "Notion", making a finished draft look stuck. The status pill still reads
  // "Drafted" (status.ts), so the rail shows done while the badge says it's a draft.
  if (state === "completed" || state === "drafted") {
    return PIPELINE_STAGES.map((s) => ({ ...s, status: "done" as const }));
  }

  // A failure (failed/aborted) freezes the rail at the furthest reached
  // milestone: that step stays `active` (PipelineProgress paints it stalled) so
  // the operator sees exactly where the run stopped.
  return PIPELINE_STAGES.map((stage, i) => {
    let status: StageStatus;
    if (activeIndex === -1) {
      // Unknown / off-path state: nothing is confidently done.
      status = "pending";
    } else if (i < activeIndex) {
      status = "done";
    } else if (i === activeIndex) {
      status = "active";
    } else {
      status = "pending";
    }
    return { ...stage, status };
  });
}

/**
 * Whether a run sits in an overlay/branch state (paused, recovering, etc.) that
 * the progress rail should call out separately. Returns the resolved meta so the
 * caller can render a badge, or null when the run is cleanly on the happy path.
 */
export function overlayState(status: string): { state: RunState; label: string } | null {
  const state = norm(status);
  if (!OVERLAY_STATES.has(state)) return null;
  return { state, label: runStatusMeta(state).label };
}
